import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { collection, doc, getDoc, getDocs, query, Timestamp, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import "../../App.css";
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import Menu from '../../panel/Menu';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function GSTreport() {
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.addEventListener('mouseenter', Swal.stopTimer)
            toast.addEventListener('mouseleave', Swal.resumeTimer)
        }
    });

    const printRef = useRef();
    const [loading, setloading] = useState(false);
    const [userData, setUserData] = useState(null);
    const [username, setusername] = useState("");
    const [fromDate, setfromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, settoDate] = useState(moment().format('YYYY-MM-DD'));

    const [bookingData, setbookingData] = useState([]);

    const allowedPayModes = [
        "MMT.COM",
        "GOIBIBO",
        "Agoda",
        "NEFT TO BANK ACCOUNT",
        "UPI BANK OF MAHARASTRA",
        "PAYTM CARD PAYMENT",
        "BOOKING.COM"
    ];

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUserData(user);
            if (user) fetchUsername(user.uid);
        });
        return () => unsubscribe();
    }, []);

    const fetchUsername = async (uid) => {
        try {
            const docSnap = await getDoc(doc(db, "users", uid));
            if (docSnap.exists()) setusername(docSnap.data().username);
        } catch (err) {
            console.error(err);
        }
    }

    const handleFromDate = (e) => setfromDate(e.target.value);
    const handleToDate = (e) => settoDate(e.target.value);

    const getReport = async () => {
        if (!fromDate || !toDate) {
            Toast.fire({ icon: "error", title: "Please select From and To Date" });
            return;
        }

        setloading(true);
        setbookingData([]);

        const startOfDay = new Date(fromDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);

        try {
            // 🔹 1. Fetch Payment History
            const payhistoryQuery = query(
                collection(db, "payhistory"),
                where("status", "==", "Working"),
                where("createdAt", ">=", Timestamp.fromDate(startOfDay)),
                where("createdAt", "<=", Timestamp.fromDate(endOfDay))
            );
            const payhistorySnapshot = await getDocs(payhistoryQuery);

            let paymentHistoryData = await Promise.all(
                payhistorySnapshot.docs.map(async (doc1) => {
                    const data = doc1.data();

                    // Safely fetch paymode1 only if data.paymode1 exists and is not empty
                    const pm1Promise = (data.paymode1 && typeof data.paymode1 === "string" && data.paymode1.trim())
                        ? getDoc(doc(db, "paymentmodes", data.paymode1))
                        : Promise.resolve(null);

                    // Safely fetch paymode2 only if data.paymode2 exists and is not empty
                    const pm2Promise = (data.paymode2 && typeof data.paymode2 === "string" && data.paymode2.trim())
                        ? getDoc(doc(db, "paymentmodes", data.paymode2))
                        : Promise.resolve(null);

                    const [pm1Snap, pm2Snap] = await Promise.all([pm1Promise, pm2Promise]);

                    const paymode1 = (pm1Snap && pm1Snap.exists()) ? pm1Snap.data().paymode : "";
                    const paymode2 = (pm2Snap && pm2Snap.exists()) ? pm2Snap.data().paymode : "";

                    return {
                        id: doc1.id,
                        data,
                        paymode1,
                        paymode2,
                        amount1: data.amount1 || 0,
                        amount2: data.amount2 || 0,
                    };
                })
            );
            // 🔹 2. Filter Allowed Pay Modes
            paymentHistoryData = paymentHistoryData.filter((p) => {
                const paymode1 = p.paymode1?.trim().toLowerCase() || "";
                const paymode2 = p.paymode2?.trim().toLowerCase() || "";
                const include = allowedPayModes.some(
                    (mode) => mode.toLowerCase() === paymode1 || mode.toLowerCase() === paymode2
                );

                return include;
            });

            // 🔹 3. Create Payment Map
            const paymentMap = {};
            paymentHistoryData.forEach((p) => {
                const bid = p.data.bookingid;
                if (!bid) {
                    console.warn("⚠️ Skipping payment record without bookingid:", p.id);
                    return;
                }
                if (!paymentMap[bid]) paymentMap[bid] = [];
                paymentMap[bid].push(p);
            });

            // 🔹 4. Fetch Checkin Details
            const checkinQuery = query(
                collection(db, "checkindetails"),
                where("checkindate", ">=", moment(fromDate).format("YYYY-MM-DD")),
                where("checkindate", "<=", moment(toDate).format("YYYY-MM-DD"))
            );
            const checkinSnapshot = await getDocs(checkinQuery);
            const checkins = checkinSnapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
            }));

            // 🔹 5. Clean booking IDs
            let bookingIds = checkins
                .map((c) => c.bookingid)
                .filter((id) => id && typeof id === "string" && id.trim() !== "");

            if (bookingIds.length === 0) {
                console.warn("⚠️ No valid booking IDs found in checkindetails.");
                setloading(false);
                return;
            }

            const chunkArray = (arr, size = 30) => {
                const chunks = [];
                for (let i = 0; i < arr.length; i += size)
                    chunks.push(arr.slice(i, i + size));
                return chunks;
            };

            // 🔹 6. Fetch Booking Details
            const bookingsDataMap = {};
            const chunks = chunkArray(bookingIds);
            for (const chunk of chunks) {
                try {
                    const bQuery = query(
                        collection(db, "bookings"),
                        where("__name__", "in", chunk)
                    );
                    const snap = await getDocs(bQuery);
                    snap.docs.forEach(
                        (d) => (bookingsDataMap[d.id] = { ...d.data(), id: d.id })
                    );
                } catch (err) {
                    console.error("❌ Error fetching bookings for chunk:", chunk, err);
                }
            }

            // 🔹 7. Combine & Filter Pending
            const combined = await Promise.all(
                checkins.map(async (c) => {
                    const booking = bookingsDataMap[c.bookingid];
                    if (!booking) return null;

                    const orderSnap = await getDocs(
                        query(
                            collection(db, "orders"),
                            where("bookingId", "==", c.bookingid),
                            where("status", "==", "Bill Pending")
                        )
                    );
                    const foodTotal = orderSnap.docs.reduce(
                        (sum, d) => sum + (d.data().totalamount || 0),
                        0
                    );

                    const paymentHistory = paymentMap[c.bookingid] || [];
                    const paidAmount = paymentHistory.reduce(
                        (sum, p) => sum + p.amount1 + p.amount2,
                        0
                    );

                    // Skip entries with 0 paid amount
                    if (paidAmount === 0) return null;

                    const roomAmount = booking.rooamount || 0;
                    const serviceAmount =
                        parseFloat(booking?.serviceamount || 0) /
                        parseFloat(booking.noofdays || 1);
                    const totalAmount = roomAmount + serviceAmount + foodTotal;

                    return {
                        ...c,
                        booking,
                        username: c.uid
                            ? (await getDoc(doc(db, "users", c.uid))).data()?.username ||
                            "Unknown User"
                            : "Unknown User",
                        roomamount: roomAmount.toFixed(0),
                        serviceamount: serviceAmount.toFixed(0),
                        foodamount: foodTotal.toFixed(0),
                        totalamount: totalAmount.toFixed(0),
                        paidamount: paidAmount.toFixed(0),
                        pending: (totalAmount - paidAmount).toFixed(0),
                        paymentHistory,
                    };
                })
            );

            // 🔹 8. Remove Duplicates
            const filtered = combined.filter((d) => d !== null);
            const uniqueData = [];
            const seen = new Set();

            for (const item of filtered) {
                if (!seen.has(item.bookingid)) {
                    seen.add(item.bookingid);
                    uniqueData.push(item);
                }
            }

            setbookingData(uniqueData);
            setloading(false);
        } catch (err) {
            console.error("❌ Error generating report:", err);
            setloading(false);
        }
    };


    const handlePrint = useReactToPrint({ content: () => printRef.current });

    const exportToExcel = () => {
        if (bookingData.length === 0) { Toast.fire({ icon: 'error', title: 'No data to export' }); return; }

        const excelData = bookingData.map((item, index) => ({
            "Sr No": index + 1,
            "Customer Name": item.booking.customers.custname,
            "Checkin Date": moment(item.checkindate).format('DD/MM/YYYY'),
            "Checkout Date": moment(item.checkoutdate).format('DD/MM/YYYY'),
            "Days": 1,
            "Rooms": item.booking.rooms.map(r => `${r.roomname}(${r.discount}%)`).join(", "),
            "Room Amount": item.roomamount,
            "Extra / Service": item.serviceamount,
            "Food": item.foodamount,
            "Total Amount": item.totalamount,
            "Payments": item.paymentHistory.map(p => {
                let s = "";
                if (p.amount1 > 0) s += `${p.paymode1}:${p.amount1} `;
                if (p.amount2 > 0) s += `${p.paymode2}:${p.amount2}`;
                return s;
            }).join(" | ")
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "GST Report");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, "GST_Report.xlsx");
    };

    return (
        <div>
            <Menu />
            <div className="content-wrapper">
                <section className="content mt-2">
                    <div className="container-fluid">
                        <button className="btn btn-secondary">|| GST Report ||</button><br /><br />
                        <div className="row">
                            <div className="col-md-12">
                                <div className="card card-primary card-outline">
                                    <div className="card-body">
                                        <div className='row'>
                                            <div className='col-sm-3'>
                                                <label>From Date</label>
                                                <input type='date' className='form-control' value={fromDate} onChange={handleFromDate} />
                                            </div>
                                            <div className='col-sm-3'>
                                                <label>To Date</label>
                                                <input type='date' className='form-control' value={toDate} onChange={handleToDate} />
                                            </div>
                                            <div className='col-sm-6'><br />
                                                <button className='btn btn-info' onClick={getReport}>Get Report</button>
                                                &nbsp;&nbsp;
                                                <button className='btn btn-success' onClick={handlePrint}>Print Report</button>
                                                &nbsp;&nbsp;
                                                <button className='btn btn-primary' onClick={exportToExcel}>Download Excel</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card card-primary card-outline" ref={printRef}>
                                    <div className="card-body">
                                        <div className="row invoice-info text-center">
                                            <div className="col-12">
                                                <h4>Dasara Executive</h4>
                                                Chatrapati Shivaji Maharaj Chouk, Tuljapur, Dist - Dharashiv, MH - 413601<br />
                                                Phone: +91 9975294940 | Email: dasaraexecutive@gmail.com | Website: www.dasaraexecutive.com
                                            </div>
                                        </div>

                                        <table className="customers mt-3">
                                            <thead>
                                                <tr>
                                                    <th>Sr No</th>
                                                    <th>Customer Name</th>
                                                    <th>Checkin Date</th>
                                                    <th>Checkout Date</th>
                                                    <th>Days</th>
                                                    <th>Rooms</th>
                                                    <th>Room Amount</th>
                                                    <th>Extra Service</th>
                                                    {/* <th>Food</th> */}
                                                    <th>Total Amount</th>
                                                    <th>Payments</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bookingData.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{item.booking.customers.custname}</td>
                                                        <td>{moment(item.checkindate).format('DD/MM/YYYY')}</td>
                                                        <td>{moment(item.checkoutdate).format('DD/MM/YYYY')}</td>
                                                        <td>1</td>
                                                        <td>{item.booking.rooms.map(r => `${r.roomname}(${r.discount}%)`).join(", ")}</td>
                                                        <td>{item.roomamount}</td>
                                                        <td>{item.serviceamount}</td>
                                                        {/* <td>{item.foodamount}</td> */}
                                                        <td>{item.totalamount}</td>
                                                        <td>
                                                            {item.paymentHistory.map((p, i) => (
                                                                <div key={i}>
                                                                    {p.amount1 > 0 && <b>{p.paymode1}: {p.amount1}</b>}<br />
                                                                    {p.amount2 > 0 && <b>{p.paymode2}: {p.amount2}</b>}
                                                                </div>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default GSTreport;
